import React, { useEffect, useMemo, useState } from 'react';
import { formatSeatNumber } from '../Util';
import { SeatStatus, SeatmapSeat, SectionSeatmap } from '../Classes';
import { Header, Icon } from 'react-frontier';
import { SeatStyle } from '@arema/types/public';
import Seatmap from './Seatmap';
import '@arema/components/style/seatmap.scss';

interface SeatmapProps{
	seatmap: SectionSeatmap,
	loading: boolean,
	error?: string,
	selected: number[],
	defaultStyle?: boolean,
	style?: React.CSSProperties,
	selectSold?: boolean,
	selectBlocked?: boolean,
	seatStyle?: SeatStyle,
	onSelected: (seats: SeatmapSeat[])=>void,
}

var SeatmapSelect = (props: SeatmapProps)=>{
	const DEBUG = parseInt(process.env.REACT_APP_DEBUG)===1;
	var { seatmap } = props;
	var map : SeatmapSeat[][] = [];
	if(seatmap && seatmap.seats){
		var has_stage = (seatmap.stage_width && seatmap.stage_width>0) && (seatmap.stage_height && seatmap.stage_height>0) && (seatmap.stage_x>=0 && seatmap.stage_y>=0);
		for(let y=0; y<seatmap.seats_height; y++){
			var row : SeatmapSeat[] = [];
			for(let x=0; x<seatmap.seats_width; x++){
				var is_stage = has_stage && (seatmap.stage_x<=x && (seatmap.stage_x+seatmap.stage_width)>x) && (seatmap.stage_y<=y && (seatmap.stage_y+seatmap.stage_height)>y);
				if(is_stage){
					row.push({
						seat_id: null,
						seat_x: x,
						seat_y: y,
						stage: true,
					});
				}else{
					var seat = props.seatmap.seats.find(a=>a.seat_x===x && a.seat_y===y);
					row.push(seat || null);
				}
			}
			map.push(row);
		}
	}

	var onSeatClick = (seat_id: number)=>{
		return ()=>{
			if(!props.onSelected) return;
			var seat = seatmap.seats.find(a=>a.seat_id==seat_id);
			if(props.selectSold!==true && !!seat.ticket_id) return;
			if(props.selectBlocked!==true && seat.seat_status!=SeatStatus.FREE) return;
			var selected_seats = props.selected ? seatmap.seats.filter(a=>props.selected.indexOf(a.seat_id)!=-1) : [];
			var ix = selected_seats.findIndex(a=>a.seat_id==seat_id);
			if(ix!=-1){
				selected_seats.splice(ix, 1);
			}else{
				selected_seats.push(seat);
			}
			props.onSelected(selected_seats);
		}
	}

	var seat_style : SeatmapProps["seatStyle"] = useMemo(()=>{
		return {
			width: props.seatStyle?.width || 60,
			height: props.seatStyle?.height || 40,
			padding: props.seatStyle?.padding || 2,
			borderRadius: props.seatStyle?.borderRadius || undefined,
			text: (typeof props.seatStyle?.text)!=='undefined' ? props.seatStyle?.text : true,
			fontSize: props.seatStyle?.fontSize || undefined,
		}
	}, [props.seatStyle]);

	return <div style={props.defaultStyle ? { flex: 1, minHeight: 600, display: 'flex', ...props.style } : props.style}>
		{props.error ? (
			<div style={{ height: 400, display: 'flex', alignItems: 'center' }}>
				<Header text='Error' subtext={props.error} containerStyle={{ flexGrow: 100 }} iconName='exclamation-circle' />
			</div>
		) : props.loading ? (
			<div style={{ height: 400, display: 'flex', alignItems: 'center' }}>
				<Header text='Cargando mapa de butacas...' loading containerStyle={{ flexGrow: 100, margin: 0 }} />
			</div>
		) : (
			<Seatmap initialScroll={Math.floor(props.seatmap.seats_width/2)*60} dragScroll>
				{map.map((row, i)=>(
					<Seatmap.Row key={`smprw-${i}`} seatAmount={props.seatmap.seats_width} seatStyle={seat_style}>
						{row.map((seat, si)=>{
							var key = `smpst-${i}-${si}`
							if(!seat){
								return <Seatmap.Seat seatStyle={seat_style} empty key={key} />
							}else if(seat.stage){
								if(seat.seat_x!=seatmap.stage_x) return null;
								var stage_row = seat.seat_y-seatmap.stage_y;
								var is_center = Math.max(Math.min(Math.floor(seatmap.stage_height/2), seatmap.stage_height), 0)==stage_row;
								var is_top = seat.seat_y==seatmap.stage_y, is_bottom = seat.seat_y==(seatmap.stage_y+seatmap.stage_height-1);

								return <Seatmap.Seat key={key} stage={[
									is_top ? 1 : 0, is_top ? 1 : 0,
									is_bottom ? 1 : 0, is_bottom ? 1 : 0,
								]} size={seatmap.stage_width} seatStyle={{
									width: seat_style.width+(seat_style.padding*2),
									height: is_top || is_bottom ? (seat_style.height+seat_style.padding) : (seat_style.height + (seat_style.padding*2))
								}} style={{
									marginTop: is_top ? seat_style.padding : 0,
									marginLeft: 0,
									marginRight: 0
								}}>
									{is_center ? 'Escenario' : null}
								</Seatmap.Seat>
							}

							var selected = props.selected.indexOf(seat.seat_id)>-1

							return <Seatmap.Seat
								selected={selected}
								seatStyle={seat_style}
								color={seat.seat_status!==SeatStatus.FREE ? 'red' : (selected ? 'orange' : (seat.special ? 'blue' : (seat.ticket_id ? 'green' : 'gray')))}
								subtext={seat_style.text ? seat.seat_section : null}
								// meta={DEBUG && seat.seat_id.toString()}
								onClick={onSeatClick(seat.seat_id)}
								style={{
									borderRadius: seat_style.borderRadius || undefined
								}}
								textStyle={{
									fontSize: seat_style.fontSize || undefined
								}}
								key={key}
							>
								{seat.seat_status===SeatStatus.LOCKED ? (
									<i className='icon lock'></i>
								) : null}
								{((seat.seat_row?.length || 0)+(seat.seat_number?.length || 0))==0 ? (
									<Icon name='chair' style={{ marginRight: 0 }} />
								) : (
									formatSeatNumber(seat.seat_row, seat.seat_number)
								)}
							</Seatmap.Seat>
						})}
					</Seatmap.Row>
				))}
			</Seatmap>
		)}
	</div>
}

export default SeatmapSelect;